import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { User, Building, Mail, CreditCard, Bell, Key, Save } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    company: user?.company || "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const updatedUser = await response.json();
        updateUser(updatedUser);
        toast.success("Settings saved successfully");
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      toast.error("Error saving settings");
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto animate-fade-in">
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900">Settings</h1>
        <p className="text-slate-600 mt-1">Manage your account settings and preferences</p>
      </div>

      {/* Profile Section */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <User className="h-5 w-5 text-slate-500" />
            Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-6">
            <Avatar className="h-20 w-20 border-4 border-slate-100">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-blue-600 text-white text-xl">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-slate-900">{user?.name}</p>
              <p className="text-sm text-slate-500">{user?.email}</p>
              <p className="text-xs text-slate-400 mt-1">
                Profile picture is synced with your Google account
              </p>
            </div>
          </div>

          <Separator />

          <div className="grid gap-4">
            <div>
              <Label>Display Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className="mt-2"
                data-testid="settings-name-input"
              />
            </div>
            <div>
              <Label>Company / Organization</Label>
              <Input
                value={formData.company}
                onChange={(e) => handleChange("company", e.target.value)}
                placeholder="Enter your company name"
                className="mt-2"
                data-testid="settings-company-input"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                value={user?.email || ""}
                disabled
                className="mt-2 bg-slate-50"
              />
              <p className="text-xs text-slate-500 mt-1">
                Email is managed by your Google account
              </p>
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="save-settings-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-slate-500" />
            Account
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900">Credits Balance</p>
              <p className="text-sm text-slate-500">Available for PR distribution</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-900">{user?.credits || 0}</p>
              <Button variant="link" className="text-blue-600 p-0 h-auto">
                Purchase More
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900">Account Type</p>
              <p className="text-sm text-slate-500">Your current plan</p>
            </div>
            <div className="text-right">
              <p className="font-semibold text-slate-900 capitalize">{user?.role || "Customer"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Bell className="h-5 w-5 text-slate-500" />
            Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-4">
            Manage how you receive notifications about your press releases.
          </p>
          <div className="space-y-3">
            {[
              { label: "PR approval/rejection updates", enabled: true },
              { label: "Distribution reports", enabled: true },
              { label: "Credit balance alerts", enabled: true },
              { label: "Marketing & promotions", enabled: false },
            ].map((pref, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 border border-slate-200 rounded-lg"
              >
                <span className="text-sm text-slate-700">{pref.label}</span>
                <span
                  className={`text-xs px-2 py-1 rounded ${
                    pref.enabled
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {pref.enabled ? "Enabled" : "Disabled"}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* API Keys (Placeholder) */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Key className="h-5 w-5 text-slate-500" />
            API Access
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-4">
            Integrate PR Link Boost with your own applications using our API.
          </p>
          <Button variant="outline">Generate API Key</Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
